import { useQuery } from 'react-query'
import { getPulsesGraph, getPulsesRanking, getNetworkPulsesGraph, getNetworkPulsesRanking } from '../Reports/apis/api-reports'
import { useReports } from '../Context/ReportsContext'
import { useCohorts } from './useCohorts'

export const usePulses = () => {
    const { reportQuery, enabled, staleTime, isNetworkContext, networkId } = useReports()
    const { courseId, cohorts, from, to } = reportQuery
    const { networkCohorts } = useCohorts()

    // Get courseIds from network cohorts if in network context
    // Filter by selected cohort dates from reportQuery
    // Note: reportQuery.cohorts has YYYY-MM-DD format, nc.startDate has ISO format
    // If cohorts is empty, use all available networkCohorts (initial state)
    const selectedCohortIds = isNetworkContext
        ? networkCohorts
            ?.filter((nc) =>
                cohorts?.length === 0 || cohorts?.includes(nc.startDate.substring(0, 10))
            )
            .map((c) => c.courseIdMongo) || []
        : []

    // Pulses Graph Query
    const pulsesFunction = isNetworkContext
        ? () => getNetworkPulsesGraph({ networkId: networkId as string, cohorts: selectedCohortIds, from, to })
        : () => getPulsesGraph(reportQuery)

    const queryKeyPulses = isNetworkContext
        ? [networkId, 'Network Reports', 'Pulses', 'Graph', `Cohorts: ${selectedCohortIds.join()}`, from, to]
        : [courseId, 'Reports', 'Pulses', 'Graph', `Cohorts: ${cohorts?.join()}`, from, to]

    const { data: pulses, isLoading: isPulsesLoading } = useQuery(queryKeyPulses, pulsesFunction, {
        enabled: isNetworkContext ? !!networkId && selectedCohortIds.length > 0 : enabled,
        staleTime,
    })

    // Ranking Query
    const rankingFunction = isNetworkContext
        ? () => getNetworkPulsesRanking({ networkId: networkId as string, cohorts: selectedCohortIds })
        : () => getPulsesRanking(reportQuery)

    const queryKeyRanking = isNetworkContext
        ? [networkId, 'Network Reports', 'Ranking', `Cohorts: ${selectedCohortIds.join()}`]
        : [courseId, 'Reports', 'Ranking', `Cohorts: ${cohorts?.join()}`, from, to]

    const { data: ranking, isLoading: isRankingLoading } = useQuery(queryKeyRanking, rankingFunction, {
        enabled: isNetworkContext ? !!networkId && selectedCohortIds.length > 0 : enabled,
        staleTime,
    })

    const columnsWidth = ranking?.reduce(
        (accu, item) => {
            const positionLength = `${item.position}`.length
            if (positionLength > accu.positionLength) accu.positionLength = positionLength
            const pulsesLength = `${item.pulsesIn}`.length
            if (pulsesLength > accu.pulsesLength) accu.pulsesLength = pulsesLength
            return accu
        },
        { positionLength: 0, pulsesLength: 0 }
    )

    return { pulses, isPulsesLoading, ranking, isRankingLoading, columnsWidth }
}
